import type { Report } from '@/models/report';
import type { NormalizedAddendum } from '../types/reportDetail';

/**
 * Normalizes the backend's inconsistent addendum shape.
 *
 * The API may return:
 *  - `report.addendum`  (singular object)
 *  - `report.addendums` (array)
 *  - neither
 *
 * This function always returns a flat NormalizedAddendum[].
 */
export function normalizeAddendums(
  report: Report | undefined
): NormalizedAddendum[] {
  if (!report) return [];

  const raw = report.addendums ?? (report.addendum ? [report.addendum] : []);

  if (!Array.isArray(raw)) return [];

  return raw.map((a) => ({
    uuid: a.uuid ?? null,
    reportUuid: a.report?.uuid ?? null,
    author:
      a.userFirstName || a.userLastName
        ? `${a.userFirstName ?? ''} ${a.userLastName ?? ''}`.trim()
        : a.userEmail || 'Unknown',
    reason: a.reason ?? '',
    createdAt: a.createdAt || a.updatedAt || '',
    text: a.description || '',
    delta: a.delta ?? null,
  }));
}

/**
 * Aggregates addendums from all study reports, filtered to the active report's
 * point in time. Deduplicates by addendum uuid and sorts oldest-first.
 */
export function normalizeStudyAddendums(
  reports: Report[],
  activeReport: Report | undefined
): NormalizedAddendum[] {
  if (!activeReport || reports.length === 0) {
    return normalizeAddendums(activeReport);
  }

  const cutoff = activeReport.createdAt;

  // Only include reports created at or before the active report,
  // excluding the active report itself to avoid double-counting
  const eligibleReports = reports.filter(
    (r) => r.createdAt <= cutoff && r.uuid !== activeReport.uuid
  );

  // Collect all addendums from eligible reports + the active report
  const all: NormalizedAddendum[] = [
    ...normalizeAddendums(activeReport),
    ...eligibleReports.flatMap(normalizeAddendums),
  ];

  // Deduplicate by uuid (keep first occurrence)
  const seen = new Set<string>();
  const unique = all.filter((a) => {
    if (!a.uuid) return true;
    if (seen.has(a.uuid)) return false;
    seen.add(a.uuid);
    return true;
  });

  // Sort oldest-first (ascending by createdAt)
  return unique.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}
